import React, { useMemo } from "react";
import MainPageWrapper from "@/components/MainPageWrapper/MainPageWrapper";
import { Button } from "@/components/Button/Button";
import { Badge } from "@/components/Badge/Badge";
import { Switch } from "@/components/ui/switch";
import DataTable from "@/components/Datatable/Datatable";
import { Column } from "@/components/Datatable/types";
import { EmployeeProfile } from "@/modules/administration/features/EmployeeDirectoryManagement/api/Profile/fetchEmployeeProfiles/types";
import { useEmployeeProfileManagementPage } from "@/modules/administration/features/EmployeeDirectoryManagement/hooks/useEmployeeProfileManagementPage";
import EmployeeProfileActionMenu from "@/modules/administration/features/EmployeeDirectoryManagement/components/Profile/EmployeeProfileActionMenu/EmployeeProfileActionMenu";
import DeleteEmployeeProfileConfirmation from "@/modules/administration/features/EmployeeDirectoryManagement/components/Profile/DeleteEmployeeProfileConfirmation/DeleteEmployeeProfileConfirmation";

const renderName = (profile: EmployeeProfile): string => {
  const first = profile.employee.firstName ?? "";
  const last = profile.employee.lastName ?? "";
  const full = `${first} ${last}`.trim();
  return full.length > 0 ? full : "—";
};

const EmployeeProfileManagementPage: React.FC = () => {
  const {
    list,
    loading,
    error,
    deleteTarget,
    goToCreate,
    goToEdit,
    handleTogglePublished,
    openDeleteModal,
    closeDeleteModal,
    handleDelete,
  } = useEmployeeProfileManagementPage();

  const columns = useMemo<Column<EmployeeProfile>[]>(
    () => [
      {
        id: "name",
        header: "Name",
        cell: ({ row }) => renderName(row.original),
      },
      {
        id: "role",
        header: "Role",
        cell: ({ row }) => row.original.role || "—",
      },
      {
        id: "specialties",
        header: "Specialties",
        cell: ({ row }) => (
          <div className="flex flex-wrap gap-1">
            {row.original.specialties.map((s) => (
              <Badge
                className="whitespace-nowrap text-white"
                key={s.uuid}
                style={{ backgroundColor: "#3B82F6" }}
                variant="secondary"
              >
                {s.name}
              </Badge>
            ))}
          </div>
        ),
      },
      {
        id: "teams",
        header: "Teams",
        cell: ({ row }) => (
          <div className="flex flex-wrap gap-1">
            {row.original.teams.map((t) => (
              <Badge
                className="whitespace-nowrap text-white"
                key={t.uuid}
                style={{ backgroundColor: "#3B82F6" }}
                variant="secondary"
              >
                {t.name}
              </Badge>
            ))}
          </div>
        ),
      },
      {
        id: "published",
        header: "Published",
        cell: ({ row }) => (
          <Switch
            checked={row.original.isPublished}
            onCheckedChange={() => handleTogglePublished(row.original)}
            onClick={(e: React.MouseEvent) => e.stopPropagation()}
          />
        ),
      },
      {
        id: "actions",
        header: "",
        cell: ({ row }) => (
          <EmployeeProfileActionMenu
            onDelete={openDeleteModal}
            onEdit={goToEdit}
            profile={row.original}
          />
        ),
      },
    ],
    [openDeleteModal, goToEdit, handleTogglePublished],
  );

  return (
    <>
      <MainPageWrapper
        actions={<Button onClick={goToCreate}>Create Profile</Button>}
        error={error}
        title="Employee Directory — Profiles"
      >
        <DataTable
          columns={columns}
          data={list}
          error={error}
          loading={loading}
          noDataMessage="No employee profiles yet."
          onPageChange={() => undefined}
          pageIndex={0}
          pageSize={list.length || 10}
          rowKeyExtractor={(item) => item.uuid}
          totalCount={list.length}
        />
      </MainPageWrapper>

      <DeleteEmployeeProfileConfirmation
        onClose={closeDeleteModal}
        onConfirm={handleDelete}
        profile={deleteTarget}
      />
    </>
  );
};

export default EmployeeProfileManagementPage;
